import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlaneIcon, MailIcon, LockIcon, UserIcon, PhoneIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Checkbox } from '../components/ui/Checkbox';
// Floating cloud component
function FloatingCloud({
  delay = 0,
  top = '20%'
}: {
  delay?: number;
  top?: string;
}) {
  return <motion.div className="absolute pointer-events-none opacity-[0.05]" style={{
    top
  }} initial={{
    x: '-20%'
  }} animate={{
    x: '120vw'
  }} transition={{
    duration: 60 + delay * 10,
    repeat: Infinity,
    ease: 'linear',
    delay
  }} data-id="element-1715">
      <svg width={120} height={60} viewBox="0 0 200 100" fill="none" data-id="element-1716">
        <ellipse cx="50" cy="60" rx="35" ry="25" fill="#0066FF" data-id="element-1717" />
        <ellipse cx="80" cy="50" rx="45" ry="30" fill="#0066FF" data-id="element-1718" />
        <ellipse cx="120" cy="55" rx="40" ry="28" fill="#0066FF" data-id="element-1719" />
        <ellipse cx="150" cy="60" rx="35" ry="25" fill="#0066FF" data-id="element-1720" />
      </svg>
    </motion.div>;
}
const benefits = ['Get refunds in under 30 minutes', '10% bonus on store credit', 'Book flights instantly', 'Track all transactions'];
export function SignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!acceptTerms) {
      setError('Please accept the terms and conditions');
      return;
    }
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    // Mock registration success
    navigate(ROUTES.HOME);
  };
  return <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 relative overflow-hidden" data-id="element-1721">
      {/* Floating Clouds */}
      <div className="fixed inset-0 pointer-events-none" data-id="element-1722">
        <FloatingCloud delay={0} top="15%" data-id="element-1723" />
        <FloatingCloud delay={15} top="45%" data-id="element-1724" />
        <FloatingCloud delay={30} top="75%" data-id="element-1725" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-primary-200 rounded-full blur-3xl opacity-50 animate-float" data-id="element-1726" />
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-accent-200 rounded-full blur-3xl opacity-50 animate-float" style={{
      animationDelay: '1s'
    }} data-id="element-1727" />

      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12" data-id="element-1728">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-2xl" data-id="element-1729">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center justify-center gap-3 mb-8" data-id="element-1730">
            <motion.div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30" whileHover={{
            scale: 1.05,
            rotate: -5
          }} data-id="element-1731">
              <PlaneIcon className="w-7 h-7 text-white" data-id="element-1732" />
            </motion.div>
            <span className="text-display-sm font-bold text-surface-900" data-id="element-1733">
              AeroZeep
            </span>
          </Link>

          {/* Signup Card */}
          <Card variant="elevated" padding="lg" className="shadow-2xl border-0" data-id="element-1734">
            <div className="text-center mb-8" data-id="element-1735">
              <h1 className="text-display-sm font-bold text-surface-900 mb-2" data-id="element-1736">
                Create Your Account
              </h1>
              <p className="text-body-md text-surface-600" data-id="element-1737">
                Join thousands of Nigerians getting fast refunds
              </p>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-2 gap-3 mb-8 p-4 bg-gradient-to-r from-primary-50 to-accent-50 rounded-xl" data-id="element-1738">
              {benefits.map((benefit, index) => <motion.div key={benefit} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center gap-2" data-id="element-1739">
                  <CheckCircleIcon className="w-4 h-4 text-success-500 flex-shrink-0" data-id="element-1740" />
                  <span className="text-body-sm text-surface-700" data-id="element-1741">
                    {benefit}
                  </span>
                </motion.div>)}
            </div>

            <form onSubmit={handleSignup} className="space-y-4" data-id="element-1742">
              <div className="grid grid-cols-2 gap-4" data-id="element-1743">
                <Input label="First Name" placeholder="Chioma" value={formData.firstName} onChange={e => handleChange('firstName', e.target.value)} leftIcon={<UserIcon className="w-4 h-4" data-id="element-1745" />} required data-id="element-1744" />
                <Input label="Last Name" placeholder="Okonkwo" value={formData.lastName} onChange={e => handleChange('lastName', e.target.value)} leftIcon={<UserIcon className="w-4 h-4" data-id="element-1747" />} required data-id="element-1746" />
              </div>

              <Input label="Email Address" type="email" placeholder="you@example.com" value={formData.email} onChange={e => handleChange('email', e.target.value)} leftIcon={<MailIcon className="w-4 h-4" data-id="element-1749" />} required data-id="element-1748" />

              <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} leftIcon={<PhoneIcon className="w-4 h-4" data-id="element-1751" />} required data-id="element-1750" />

              <div className="relative" data-id="element-1752">
                <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="At least 8 characters" value={formData.password} onChange={e => handleChange('password', e.target.value)} leftIcon={<LockIcon className="w-4 h-4" data-id="element-1754" />} required data-id="element-1753" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-surface-400 hover:text-surface-600 transition-colors" data-id="element-1755">
                  {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-1756" /> : <EyeIcon className="w-4 h-4" data-id="element-1757" />}
                </button>
              </div>

              <div className="relative" data-id="element-1758">
                <Input label="Confirm Password" type={showConfirmPassword ? 'text' : 'password'} placeholder="Re-enter your password" value={formData.confirmPassword} onChange={e => handleChange('confirmPassword', e.target.value)} leftIcon={<LockIcon className="w-4 h-4" data-id="element-1760" />} required data-id="element-1759" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-[38px] text-surface-400 hover:text-surface-600 transition-colors" data-id="element-1761">
                  {showConfirmPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-1762" /> : <EyeIcon className="w-4 h-4" data-id="element-1763" />}
                </button>
              </div>

              {error && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-3 bg-error-50 border border-error-200 rounded-lg" data-id="element-1764">
                  <p className="text-body-sm text-error-700" data-id="element-1765">{error}</p>
                </motion.div>}

              <Checkbox id="terms" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} label={<span className="text-body-sm text-surface-600" data-id="element-1767">
                    I agree to the{' '}
                    <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium" data-id="element-1768">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary-600 hover:text-primary-700 font-medium" data-id="element-1769">
                      Privacy Policy
                    </Link>
                  </span>} data-id="element-1766" />

              <Button type="submit" fullWidth loading={isLoading} icon={<ArrowRightIcon className="w-4 h-4" data-id="element-1771" />} iconPosition="right" className="shadow-lg shadow-primary-500/20" data-id="element-1770">
                Create Account
              </Button>
            </form>

            {/* Sign In Link */}
            <div className="mt-6 text-center" data-id="element-1772">
              <p className="text-body-sm text-surface-600" data-id="element-1773">
                Already have an account?{' '}
                <Link to={ROUTES.LOGIN} className="font-semibold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-1774">
                  Sign in
                </Link>
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>;
}